import { useEffect, useState } from 'react';
import WeatherService from '../services/weatherService';
import { getLocalDateString } from '../utils/dateUtils';

// One forecast per place per quarter hour: the hero re-renders often (the
// greeting follows the clock) and the service's own cache is 30 minutes.
const cache = new Map();
const TTL_MS = 15 * 60 * 1000;

const keyFor = (coords) => `${coords.lat.toFixed(2)},${coords.lng.toFixed(2)}`;

/**
 * The real forecast for one date at one place — today at home for the hero's
 * greeting line, the first day of the trip for a trip hero within the
 * forecast's week. Returns a WeatherService day object (high, condition,
 * precipitation.probability) or null: outside the ~8-day forecast, with no
 * coordinates, or while loading, the line simply omits the weather. Never a
 * typical-month figure, which would read as a forecast.
 */
export default function useTodayForecast(coords, name, date = getLocalDateString(new Date())) {
  const lat = Number(coords?.lat);
  const lng = Number(coords?.lng);
  const usable = Number.isFinite(lat) && Number.isFinite(lng);
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!usable) { setWeather(null); return undefined; }
    const key = keyFor({ lat, lng });
    const hit = cache.get(key);
    let alive = true;
    const commit = (byDate) => { if (alive) setWeather(byDate?.[date] || null); };
    if (hit && Date.now() - hit.ts < TTL_MS) {
      commit(hit.byDate);
      return () => { alive = false; };
    }
    // A new place shows no figure until its own arrives: the old place's
    // temperature under the new place's name is a wrong fact, not a loading state.
    setWeather(null);
    WeatherService.getForecastByDate(name || key, { latitude: lat, longitude: lng })
      .then((byDate) => {
        cache.set(key, { byDate: byDate || {}, ts: Date.now() });
        commit(byDate);
      })
      .catch(() => commit(null));
    return () => { alive = false; };
  }, [usable, lat, lng, name, date]);

  return usable ? weather : null;
}
